import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, ConfirmDialog, Flex, Text, Tooltip } from 'core/components';
import { UdeFormProps } from 'app/views/boards/panels/ude/form.types';
import { TbChevronsLeft, TbHistory, TbX } from 'react-icons/tb';
import { useDictionaryMeasurements } from 'app/stores/dictionary/useDictionaryMeasurements';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import { checkMeasurement } from '@kentik/ui-shared/query/measurements';
import { isNonSpecializingDimension } from '@kentik/ui-shared/query/dimensionUtils';
import { useQueryHistory } from './history/useQueryHistory';
import { useUdeNlq } from './ai/useUdeNlq';
import CategoryPanel, { CATEGORIES, SearchResultItem } from './CategoryPanel';
import SelectedPanel from './SelectedPanel';
import { addDimensionToForm, addMetricToForm, applyDefaultQuery } from './selectionHandlers';
import { titleExactMatches } from './metricsTreeData';
import UdeQueryHistoryList from './history/UdeQueryHistoryList';
import SidebarSection from '../../SidebarSection';
import UdeFilterModal from './filters/UdeFilterModal';
import UdeFilterDisplay from './filters/UdeFilterDisplay';
import BoardVariablesContext, { AdvancedVariableMapping } from './filters/BoardVariablesContext';
import PanelVariableMapping from '../../PanelVariableMapping';

/** Extract the top-level category prefix from a full measurement path */
function getCategoryPrefix(measurementPath: string): string | null {
  // e.g. '/metrics/interfaces/counters' → '/metrics'
  const match = measurementPath.match(/^(\/[^/]+)/);
  return match ? match[1] : null;
}

/** Get display label for a category prefix */
function getCategoryLabel(prefix: string): string {
  const category = CATEGORIES.find((c) => c.key === prefix);
  return category?.label ?? prefix.replace('/', '');
}

/** Format a measurement path for display in the header */
function formatMeasurementDisplay(measurement: string, prefix: string): string {
  if (measurement === prefix) {
    return getCategoryLabel(prefix);
  }
  const segments = measurement.replace(prefix, '').split('/').filter(Boolean);
  const formatted = segments
    .map((seg) => titleExactMatches[seg] || seg.charAt(0).toUpperCase() + seg.slice(1))
    .join(' / ');
  return `${getCategoryLabel(prefix)} / ${formatted}`;
}

const UdePanelForm = (
  props: UdeFormProps & {
    panel: BoardPanelModel;
    onToggleCollapse?: () => void;
    queryResetSignal?: number;
  }
) => {
  const { form, onRunQuery, onSave, panel, onToggleCollapse, queryResetSignal } = props;
  const nlq = useUdeNlq();
  const handleAutoTitle = useCallback(
    (title: string) => {
      form.setValue('title', title as never);
      // Persist title as draft — does not navigate (handleSavePanel guards on !options.draft)
      onSave({ title }, { draft: true, toast: false });
    },
    [form, onSave]
  );
  const history = useQueryHistory({
    panel,
    summarize: nlq.summarize,
    generateTitle: nlq.generateTitle,
    onAutoTitle: handleAutoTitle
  });
  const formValues = form.getValues();
  const measurementName = form.getValue('config.data.measurement');
  const lookback = form.getValue('config.data.time.lookback');
  const metrics = form.getValue('config.data.metrics') || [];
  const dimensions = form.getValue('config.data.dimensions') || [];

  const { measurements, measurementHash, appProtocols } = useDictionaryMeasurements({ lookback });

  // Filter modal state
  const [isEditingFilters, setIsEditingFilters] = useState(false);

  const handleToggleFilterModal = useCallback(() => {
    setIsEditingFilters((prev) => !prev);
  }, []);

  // Category prefix tracked separately (can be set before full measurement)
  const [categoryPrefix, setCategoryPrefix] = useState<string | null>(() => {
    if (measurementName) {
      return getCategoryPrefix(measurementName);
    }
    return null;
  });

  // Sync categoryPrefix when measurement changes externally (e.g. from the empty-state AI input).
  // getCategoryPrefix is deterministic, and the nextPrefix !== categoryPrefix guard prevents loops.
  useEffect(() => {
    if (!measurementName) {
      return;
    }
    const nextPrefix = getCategoryPrefix(measurementName);
    if (nextPrefix && nextPrefix !== categoryPrefix) {
      setCategoryPrefix(nextPrefix);
    }
  }, [measurementName, categoryPrefix]);

  useEffect(() => {
    if (checkMeasurement.isTraffic(measurementName)) {
      // Collect dimension names from the query's top-level selected dimensions
      type RawDimension = { name?: string };
      const queryDimNames: string[] = (
        (formValues as { config?: { data?: { dimensions?: RawDimension[] } } })?.config?.data?.dimensions ?? []
      )
        .map((d) => d.name)
        .filter((n): n is string => Boolean(n));

      // Recursively collect filterField names from nested dimension filter groups
      const filterDimNames: string[] = [];
      type RawFilterGroup = { filters?: { filterField?: string }[]; filterGroups?: RawFilterGroup[] };
      const collectFilterFields = (group: RawFilterGroup | null | undefined): void => {
        if (group) {
          for (const f of group.filters ?? []) {
            if (f.filterField) {
              filterDimNames.push(f.filterField);
            }
          }
          for (const nested of group.filterGroups ?? []) {
            collectFilterFields(nested);
          }
        }
      };
      const filtersDimensions = (formValues as { config?: { data?: { filters?: { dimensions?: RawFilterGroup } } } })
        ?.config?.data?.filters?.dimensions;
      collectFilterFields(filtersDimensions);

      const allDimNames = [...queryDimNames, ...filterDimNames];

      // Find the most specific traffic measurement that contains any of the collected dimension names
      // If we find one that is not /traffic, we will set it and be done.
      for (const dimName of allDimNames) {
        if (!isNonSpecializingDimension(dimName)) {
          for (const m of measurements) {
            const { measurement: currMeasurementName } = m;
            if (checkMeasurement.isTrafficChild(currMeasurementName) && dimName in (m.storage?.Dimensions ?? {})) {
              if (measurementName !== currMeasurementName) {
                form.setValue('config.data.measurement', currMeasurementName);
              }
              return;
            }
          }
        }
      }

      // If we got here and we weren't already set to /traffic, let's do that.
      if (!checkMeasurement.isTrafficRoot(measurementName)) {
        form.setValue('config.data.measurement', '/traffic');
      }
    }
  }, [form, formValues, measurementName, measurements]);

  // Confirmation dialog when changing category with existing selections
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);

  const handleReset = useCallback(() => {
    // Reset query data fields
    form.fieldStates.forEach((field) => {
      if (
        /^config\.data\.(dimensions|filters|measurement|metrics|reAgg|rollups|sort|window)/.test(field.originalName)
      ) {
        field.setToDefault({ validate: false });
      }
    });

    // Reset chart config fields dependent on data fields
    form.setValue('config.chart.yAxis.metric', '');
    form.setValue('config.chart.yAxis.aggregation', '');
    form.setValue('config.chart.yAxis.multipleAxis', false);
  }, [form]);

  // When the parent triggers a query reset (e.g. the Cancel button), return the
  // sidebar to the category picker. The form fields and dataview are already
  // cleared by the parent, so we only reset local UI state here.
  const isInitialResetSignal = useRef(true);
  useEffect(() => {
    if (isInitialResetSignal.current) {
      isInitialResetSignal.current = false;
      return;
    }
    setCategoryPrefix(null);
  }, [queryResetSignal]);

  const applyCategory = useCallback(
    (prefix: string) => {
      handleReset();
      setCategoryPrefix(prefix);

      // If this prefix directly corresponds to a measurement, set it so the user can
      // start adding units/dimensions.
      const matchingMeasurement = measurements.find((m) => m.measurement === prefix);
      if (matchingMeasurement) {
        form.setValue('config.data.measurement', prefix);

        if (prefix !== '/traffic') {
          // For all measurements other than /traffic, apply the default query. For
          // /traffic we intentionally do NOT auto-apply defaults — the user should
          // choose their own units/dimensions.
          applyDefaultQuery(form, prefix, measurements);
        }
      }
    },
    [handleReset, form, measurements]
  );

  const handleCategorySelect = useCallback(
    (prefix: string) => {
      const hasSelections = metrics.length > 0 || dimensions.length > 0;
      let currentTopLevel: string | null;
      if (measurementName) {
        currentTopLevel = getCategoryPrefix(measurementName);
      } else if (categoryPrefix) {
        currentTopLevel = getCategoryPrefix(categoryPrefix);
      } else {
        currentTopLevel = null;
      }
      const incomingTopLevel = getCategoryPrefix(prefix);

      // If switching top-level category with existing selections, confirm first
      if (currentTopLevel && hasSelections && currentTopLevel !== incomingTopLevel) {
        setPendingCategory(prefix);
        return;
      }

      applyCategory(prefix);
    },
    [metrics.length, dimensions.length, measurementName, categoryPrefix, applyCategory]
  );

  const handleConfirmCategoryChange = useCallback(() => {
    if (pendingCategory) {
      applyCategory(pendingCategory);
      setPendingCategory(null);
    }
  }, [pendingCategory, applyCategory]);

  const handleCancelCategoryChange = useCallback(() => {
    setPendingCategory(null);
  }, []);

  /** Handle selecting an item directly from the category search results */
  const handleSearchSelect = useCallback(
    (item: SearchResultItem) => {
      // Set category + measurement
      handleReset();
      setCategoryPrefix(item.categoryPrefix);
      form.setValue('config.data.measurement', item.measurement);

      // Add the item to the form
      if (item.type === 'metric') {
        addMetricToForm(form, item.value, measurements);
      } else {
        addDimensionToForm(form, item.value);
      }

      // Apply defaults for this measurement
      applyDefaultQuery(form, item.measurement, measurements);
    },
    [form, measurements, handleReset]
  );

  /** Measurement display label for the persistent header */
  const measurementDisplayLabel = useMemo(() => {
    if (!categoryPrefix) {
      return null;
    }
    const topLevel = getCategoryPrefix(categoryPrefix);
    if (measurementName && topLevel) {
      return formatMeasurementDisplay(measurementName, topLevel);
    }
    if (topLevel && categoryPrefix !== topLevel) {
      // Sub-category selected but no measurement yet: show "NMS / Interfaces"
      return formatMeasurementDisplay(categoryPrefix, topLevel);
    }
    return getCategoryLabel(categoryPrefix);
  }, [measurementName, categoryPrefix]);

  const handleSelectHistoryEntry = useCallback(
    (index: number) => {
      if (!history) {
        return;
      }
      const query = history.goToEntry(index);
      if (query) {
        // Restore the measurement for the sidebar
        if (query.measurement) {
          setCategoryPrefix(query.measurement);
        }
        // Restore all query fields, clearing any that are absent in the snapshot
        form.setValue('config.data.measurement', (query.measurement ?? '') as never);
        form.setValue('config.data.metrics', (query.metrics ?? []) as never);
        form.setValue('config.data.dimensions', (query.dimensions ?? []) as never);
        form.setValue('config.data.time', (query.time ?? {}) as never);
        form.setValue('config.data.filters', (query.filters ?? {}) as never);
        form.setValue('config.data.rollups', (query.rollups ?? []) as never);
        form.setValue('config.data.sort', (query.sort ?? []) as never);

        // Restore chart config if present
        const entry = history.entries[index];
        if (entry?.chart) {
          form.setValuesDeep({ config: { chart: entry.chart } } as never);
        }

        history.toggleOpen(); // Close history panel
        onRunQuery?.(); // Execute the restored query
      }
    },
    [history, form, onRunQuery]
  );

  const handleRemoveEntry = useCallback(
    (index: number) => {
      history?.removeEntry(index);
    },
    [history]
  );

  const handleSetToHead = useCallback(
    (index: number) => {
      if (!history) {
        return;
      }
      const query = history.setToHead(index);
      if (query) {
        if (query.measurement) {
          setCategoryPrefix(query.measurement);
        }
        // Restore all query fields, clearing any that are absent in the snapshot
        form.setValue('config.data.measurement', (query.measurement ?? '') as never);
        form.setValue('config.data.metrics', (query.metrics ?? []) as never);
        form.setValue('config.data.dimensions', (query.dimensions ?? []) as never);
        form.setValue('config.data.time', (query.time ?? {}) as never);
        form.setValue('config.data.filters', (query.filters ?? {}) as never);
        form.setValue('config.data.rollups', (query.rollups ?? []) as never);
        form.setValue('config.data.sort', (query.sort ?? []) as never);

        const headEntry = history.entries[0];
        if (headEntry?.chart) {
          form.setValuesDeep({ config: { chart: headEntry.chart } } as never);
        }

        onRunQuery?.();
      }
    },
    [history, form, onRunQuery]
  );

  // Check if dimension filters exist to toggle "Add" vs "Edit" button text
  const dimensionFilterGroups = form.getValue('config.data.filters.dimensions.filterGroups') || [];
  const hasDimensionFilters =
    Array.isArray(dimensionFilterGroups) &&
    dimensionFilterGroups.length > 0 &&
    dimensionFilterGroups.some((group: { filters?: unknown[] }) => (group.filters?.length ?? 0) > 0);

  const boardVariables = panel.board.get('config')?.variables ?? [];

  // All enabled board variables are available as {{variableName}} substitution options in filter
  // value inputs — both custom and guided. Reading board observable directly so MobX re-renders on change.
  const advancedMappings: AdvancedVariableMapping[] = boardVariables
    .filter((v) => v.enabled !== false)
    .map((v) => ({ variableName: v.name }));

  return (
    <Flex className="UdePanelForm" flexDirection="column" flex={1} style={{ minHeight: 0 }}>
      {!categoryPrefix ? (
        <CategoryPanel
          selectedCategory={categoryPrefix}
          onSelectCategory={handleCategorySelect}
          measurements={measurements}
          onSearchSelect={handleSearchSelect}
          onToggleCollapse={onToggleCollapse}
        />
      ) : null}

      {categoryPrefix && (
        <SidebarSection
          defaultIsOpen
          className={`ude-measurement-header ${history?.isOpen ? '' : 'ude-query-header'}`}
          isCollapsible={false}
          borderBottom="none"
          iconProps={{ color: 'kentik' }}
          pl="8px"
          pr="4px"
          title={
            <Flex flex={1} minWidth={0} overflow="hidden" alignItems="center">
              <Text as="div" fontSize={14} fontWeight="bold">
                {history?.isOpen ? 'Query History' : 'Query'}
              </Text>
            </Flex>
          }
          rightElement={
            <Flex gap="4px" ml="auto" alignItems="center">
              {history?.isOpen ? (
                <Button minimal icon={TbX} onClick={history.toggleOpen} aria-label="Close history" />
              ) : (
                <>
                  {history && history.entries.length > 0 && (
                    <Tooltip content="Query history">
                      <Button minimal icon={TbHistory} onClick={history.toggleOpen} aria-label="Query history" />
                    </Tooltip>
                  )}
                  {onToggleCollapse && (
                    <Tooltip content="Close Query options">
                      <Button
                        minimal
                        icon={TbChevronsLeft}
                        onClick={onToggleCollapse}
                        aria-label="Close Query options"
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </Flex>
          }
        >
          {!history?.isOpen && (
            <Flex
              bg="sectionBackground"
              p="8px 12px 8px 8px"
              mb={2}
              borderBottom="thin"
              alignItems="center"
              minWidth={0}
              overflow="hidden"
            >
              <Tooltip content={measurementDisplayLabel} hoverOpenDelay={600}>
                <Text
                  as="div"
                  fontSize={12}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {measurementDisplayLabel}
                </Text>
              </Tooltip>
            </Flex>
          )}
          {history?.isOpen ? (
            <UdeQueryHistoryList
              entries={history.entries}
              headIndex={history.headIndex}
              onSelectEntry={handleSelectHistoryEntry}
              onRemoveEntry={handleRemoveEntry}
              onSetToHead={handleSetToHead}
              onClose={history.toggleOpen}
              hideHeader
            />
          ) : (
            measurementName && (
              <SelectedPanel
                form={form}
                measurements={measurements}
                measurementHash={measurementHash}
                appProtocols={appProtocols}
                onReset={handleReset}
              />
            )
          )}
        </SidebarSection>
      )}

      {categoryPrefix && measurementName && !history?.isOpen && (
        <BoardVariablesContext.Provider value={advancedMappings}>
          <SidebarSection
            defaultIsOpen
            isCollapsible={false}
            borderBottom="none"
            iconProps={{ color: 'kentik' }}
            pr={1}
            title={
              <Text fontSize={13} fontWeight="bold">
                FILTERS
              </Text>
            }
            rightElement={
              <Button
                small
                minimal
                intent="primary"
                text={hasDimensionFilters ? 'Edit' : 'Add'}
                onClick={handleToggleFilterModal}
                ml="auto"
              />
            }
          >
            <Flex flexDirection="column" px="12px" pb="8px" gap={1}>
              <UdeFilterDisplay
                fieldName="config.data.filters.dimensions"
                measurement={measurementName}
                measurements={measurements}
              />
              <UdeFilterModal
                title="Filters"
                fieldName="config.data.filters.dimensions"
                isOpen={isEditingFilters}
                onClose={handleToggleFilterModal}
                measurement={measurementName}
                measurements={measurements}
                measurementHash={measurementHash}
              />
            </Flex>
          </SidebarSection>
        </BoardVariablesContext.Provider>
      )}

      {categoryPrefix && measurementName && boardVariables.length > 0 && (
        <SidebarSection
          defaultIsOpen
          isCollapsible={false}
          borderBottom="none"
          iconProps={{ color: 'kentik' }}
          pr={1}
          title={
            <Text fontSize={13} fontWeight="bold">
              FILTERS FROM DASHBOARD
            </Text>
          }
        >
          <Flex flexDirection="column" px="12px" pb="8px" gap="2px">
            {boardVariables.map((bv) => (
              <PanelVariableMapping key={bv.name} form={form} measurements={measurements} panel={panel} variable={bv} />
            ))}
          </Flex>
        </SidebarSection>
      )}

      <ConfirmDialog
        isOpen={!!pendingCategory}
        title="Change Category?"
        text="Changing the category will clear your current selections. Continue?"
        confirmButtonText="Change Category"
        confirmButtonIntent="danger"
        onConfirm={handleConfirmCategoryChange}
        onCancel={handleCancelCategoryChange}
      />
    </Flex>
  );
};

UdePanelForm.displayName = 'UdePanelForm';

export default observer(UdePanelForm);
